import React from "react";

/**
 * Template #8 — guide_actor (3D-style character)
 * Human guidance, emotional reinforcement.
 * Parts: head, arm, body
 * Actions: enter, exit, point, react
 * Emotions: neutral, explaining, curious, confident, celebrate, caution
 *
 * 3D rounded character with depth shading, glowing accents, and shadow.
 */

export interface GuideActorProps {
  x?: number;
  y?: number;
  scale?: number;
  emotion?: "neutral" | "explaining" | "curious" | "confident" | "celebrate" | "caution";
  pointDirection?: "left" | "right" | "up" | "none";
  opacity?: number;
  accentColor?: string;
}

export const GuideActor: React.FC<GuideActorProps> = ({
  x = 700,
  y = 380,
  scale: s = 1,
  emotion = "neutral",
  pointDirection = "none",
  opacity = 1,
  accentColor = "#818cf8",
}) => {
  const uid = "ga";

  const getEyes = () => {
    switch (emotion) {
      case "explaining": return { left: "◐", right: "◐" };
      case "curious":    return { left: "◑", right: "◐" };
      case "confident":  return { left: "━", right: "━" };
      case "celebrate":  return { left: "★", right: "★" };
      case "caution":    return { left: "!", right: "!" };
      default:           return { left: "·", right: "·" };
    }
  };

  const getMouth = () => {
    switch (emotion) {
      case "explaining": return "○";
      case "curious":    return "○";
      case "confident":  return "⌣";
      case "celebrate":  return "▽";
      case "caution":    return "—";
      default:           return "⌣";
    }
  };

  const getLeftArm = () => {
    if (pointDirection === "left") return { x2: -32, y2: -22 };
    if (emotion === "celebrate") return { x2: -20, y2: -32 };
    return { x2: -14, y2: 18 };
  };

  const getRightArm = () => {
    if (pointDirection === "right") return { x2: 32, y2: -22 };
    if (pointDirection === "up") return { x2: 14, y2: -38 };
    if (emotion === "celebrate") return { x2: 20, y2: -32 };
    if (emotion === "explaining") return { x2: 22, y2: -12 };
    return { x2: 14, y2: 18 };
  };

  const la = getLeftArm();
  const ra = getRightArm();

  return (
    <g opacity={opacity} transform={`translate(${x}, ${y}) scale(${s})`}>
      <defs>
        <radialGradient id={`${uid}-head`} cx="40%" cy="35%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.15" />
          <stop offset="50%" stopColor="#334155" />
          <stop offset="100%" stopColor="#0f172a" />
        </radialGradient>
        <linearGradient id={`${uid}-body`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#e2e8f0" />
          <stop offset="100%" stopColor="#94a3b8" />
        </linearGradient>
      </defs>

      {/* Ground shadow */}
      <ellipse cx={2} cy={4} rx={22} ry={5} fill="#000" opacity={0.2} />

      {/* Ambient glow */}
      <circle cx={0} cy={-58} r={50} fill={accentColor} opacity={0.04} />

      {/* Body (3D rounded limb style) */}
      <line x1={0} y1={-80} x2={0} y2={-30} stroke={`url(#${uid}-body)`} strokeWidth={4} strokeLinecap="round" />
      <line x1={1} y1={-78} x2={1} y2={-32} stroke="#fff" strokeWidth={1} opacity={0.08} />

      {/* Legs */}
      <line x1={0} y1={-30} x2={-11} y2={0} stroke={`url(#${uid}-body)`} strokeWidth={3.5} strokeLinecap="round" />
      <line x1={0} y1={-30} x2={11} y2={0} stroke={`url(#${uid}-body)`} strokeWidth={3.5} strokeLinecap="round" />
      {/* Shoe dots */}
      <circle cx={-11} cy={1} r={3} fill="#475569" />
      <circle cx={11} cy={1} r={3} fill="#475569" />

      {/* Left arm */}
      <line x1={0} y1={-65} x2={la.x2} y2={-65 + la.y2} stroke={`url(#${uid}-body)`} strokeWidth={3} strokeLinecap="round" />
      {/* Right arm */}
      <line x1={0} y1={-65} x2={ra.x2} y2={-65 + ra.y2} stroke={`url(#${uid}-body)`} strokeWidth={3} strokeLinecap="round" />

      {/* Pointing hand glow */}
      {pointDirection !== "none" && (
        <g>
          <circle
            cx={pointDirection === "left" ? la.x2 : ra.x2}
            cy={pointDirection === "left" ? -65 + la.y2 : -65 + ra.y2}
            r={5}
            fill={accentColor}
            opacity={0.25}
          />
          <circle
            cx={pointDirection === "left" ? la.x2 : ra.x2}
            cy={pointDirection === "left" ? -65 + la.y2 : -65 + ra.y2}
            r={3}
            fill={accentColor}
          />
        </g>
      )}

      {/* Head — 3D sphere */}
      <circle cx={0} cy={-96} r={16} fill={`url(#${uid}-head)`} stroke="#94a3b8" strokeWidth={1.5} />
      {/* Specular */}
      <circle cx={-4} cy={-102} r={4} fill="#fff" opacity={0.12} />

      {/* Eyes */}
      <text x={-5} y={-97} fill={accentColor} textAnchor="middle" fontSize={7} fontWeight={700} fontFamily="system-ui, sans-serif">
        {getEyes().left}
      </text>
      <text x={5} y={-97} fill={accentColor} textAnchor="middle" fontSize={7} fontWeight={700} fontFamily="system-ui, sans-serif">
        {getEyes().right}
      </text>

      {/* Mouth */}
      <text x={0} y={-87} fill="#94a3b8" textAnchor="middle" fontSize={6} fontFamily="system-ui, sans-serif">
        {getMouth()}
      </text>

      {/* Emotion bubble */}
      {(emotion === "explaining" || emotion === "caution") && (
        <g>
          <circle cx={20} cy={-112} r={10} fill="#1e293b" stroke={accentColor} strokeWidth={1.2} />
          <circle cx={18} cy={-114} r={3} fill="#fff" opacity={0.06} />
          <text x={20} y={-109} fill={accentColor} textAnchor="middle" fontSize={8} fontWeight={700} fontFamily="system-ui, sans-serif">
            {emotion === "explaining" ? "!" : "⚠"}
          </text>
        </g>
      )}

      {/* Celebrate particles */}
      {emotion === "celebrate" && (
        <g>
          {[
            { cx: -22, cy: -115, r: 2.5 },
            { cx: 24, cy: -120, r: 2 },
            { cx: -16, cy: -125, r: 1.5 },
            { cx: 18, cy: -108, r: 2 },
            { cx: -8, cy: -130, r: 1.5 },
            { cx: 10, cy: -128, r: 1 },
          ].map((sp, i) => (
            <circle key={i} cx={sp.cx} cy={sp.cy} r={sp.r} fill={i % 2 === 0 ? accentColor : "#facc15"} opacity={0.7}>
              <animate attributeName="opacity" values="0.7;0.2;0.7" dur={`${1.2 + i * 0.25}s`} repeatCount="indefinite" />
              <animate attributeName="cy" values={`${sp.cy};${sp.cy - 4};${sp.cy}`} dur={`${1 + i * 0.3}s`} repeatCount="indefinite" />
            </circle>
          ))}
        </g>
      )}
    </g>
  );
};
